import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Gift, Logout, Person } from '@clds/icon';
import { HorizontalSeparator } from '@clds/separator';
import { defaultLogOut } from '@cld/session-service';
import type { CloseTrigger } from '@cld/share-with-a-friend-modal';
import ShareWithAFriendModal from '@cld/share-with-a-friend-modal';
import { AnalyticsWrapper, useAnalytics } from '@cld/analytics';
import { DropdownMenuItem, DropdownMenuItemWithLink, UserAvatar } from '../../components';
import { getUserName } from '../../components/UserAvatar/UserAvatarTooltip';
import { useUserData } from '../../context';
import { useApplicationWrapperContext } from '../../context/applicationWrapper/ApplicationWrapper.context';
import { DropdownItemNonInteractive } from '../../components/DropdownMenuItem/DropdownMenuItem';
import { MENU_ITEM_ICON_SIZE } from '../../ApplicationWrapper.const';
import { useCustomerIdContext } from '../../context';
import { messages } from '../../i18n';
import type { SocialInvitation } from '../../api/socialInvitation/fetchSocialInvitation';
import { creatFetchSocialInvitation } from '../../api/socialInvitation/fetchSocialInvitation';
import { getSettingsHref } from '../BottomPrimaryMenuItems/utils';
import { UserAdminItems } from './UserAdminItems';
import { useUnicornApiContext } from '../../UnicornApi.context';

export const UserDropdownMenuItems: React.FC<{ isMobile?: boolean }> = ({ isMobile = false }) => {
  const { formatMessage } = useIntl();
  const { logOut = defaultLogOut } = useApplicationWrapperContext();
  const { unicornApi } = useUnicornApiContext();
  const { userData: user } = useUserData();
  const analytics = useAnalytics('primary menu');
  const planName = user?.planInfos[0]?.plan_name ?? '';
  const isFreePlan = ['free', 'free_classic'].includes(planName);
  const fetchClient = useMemo(() => unicornApi?.methods.createFetchClient() ?? window.fetch, [unicornApi?.methods]);
  const fetchSocialInvitation = useMemo(() => creatFetchSocialInvitation(fetchClient), [fetchClient]);
  const showInviteAFriendButton = useMemo(() => user?.planInfos && isFreePlan && !isMobile, [user, isFreePlan]);
  const [socialInvitation, setSocialInvitation] = useState<SocialInvitation | null>(null);
  const { currentCustomer } = useCustomerIdContext();
  const customerId = useMemo(() => currentCustomer?.id, [currentCustomer]);
  const externalCustomerId = useMemo(() => currentCustomer?.externalId, [currentCustomer]);

  const [isInvitationModalOpen, setInvitationModalOpen] = useState(false);

  useEffect(() => {
    if (customerId && externalCustomerId && showInviteAFriendButton) {
      void fetchSocialInvitation(customerId).then((data) => setSocialInvitation(data));
    }
  }, [externalCustomerId, customerId, showInviteAFriendButton, fetchSocialInvitation]);

  return (
    <AnalyticsWrapper category="user menu">
      <DropdownItemNonInteractive
        leftSlot={<UserAvatar userInfo={user?.userInfo} />}
        secondaryContent={<span data-test-specifier="user avatar - user email">{user?.userInfo?.email}</span>}
      >
        <span data-test-specifier="user avatar - user name">{getUserName(user?.userInfo)}</span>
      </DropdownItemNonInteractive>
      <HorizontalSeparator />
      <DropdownMenuItemWithLink
        data-test-specifier={'my profile'}
        analyticsMessage="profile settings"
        analyticsAction="navigate"
        href={`${getSettingsHref(currentCustomer)}/profile`}
        leftSlot={<Person size={MENU_ITEM_ICON_SIZE} />}
      >
        {formatMessage(messages.userMyProfile)}
      </DropdownMenuItemWithLink>
      {user?.isCldAdmin && (
        <>
          <UserAdminItems />
        </>
      )}
      {showInviteAFriendButton && (
        <>
          <DropdownMenuItemWithLink
            analyticsMessage="invite a friend"
            analyticsAction="open modal"
            data-test-specifier="share with a friend button"
            onClick={() => setInvitationModalOpen(true)}
            leftSlot={<Gift size={MENU_ITEM_ICON_SIZE} />}
          >
            {formatMessage(messages.shareWithAFriend)}
          </DropdownMenuItemWithLink>

          {socialInvitation && (
            <ShareWithAFriendModal
              open={isInvitationModalOpen}
              onClose={
                /* istanbul ignore next */ (reason: CloseTrigger) => {
                  setInvitationModalOpen(false);
                  analytics(`invite a friend modal | close using ${reason === 'x button' ? 'x' : 'close'} button`, 'close');
                }
              }
              onCopy={
                /* istanbul ignore next */ () => {
                  analytics('copy to clipboard', 'invite a friend modal | copy invite link');
                }
              }
              title={formatMessage(messages.shareWithAFriendModalTitle)}
              content={formatMessage(messages.shareWithAFriendModalContent)}
              note={formatMessage(messages.shareWithAFriendModalNote)}
              link={socialInvitation?.cloudinary.url}
            />
          )}
        </>
      )}
      <HorizontalSeparator />
      <DropdownMenuItem data-test-specifier="logout" analyticsMessage="logout" leftSlot={<Logout />} onClick={logOut}>
        {formatMessage(messages.userLogout)}
      </DropdownMenuItem>
    </AnalyticsWrapper>
  );
};
